import React, { useEffect, useState } from "react";
import { BellPlus, X, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { createPriceAlert } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * QuickAlertModal — Reusable modal for creating any custom alert kind:
 *   price_threshold | percent_move | breakeven | ltv_threshold | sofr_spread
 *
 * Pass either:
 *   - `defaults` to prefill the form (mode is fixed)
 *   - or open in default `price_threshold` mode with optional symbol
 *
 * Pro-only — non-Pro users see a CTA instead.
 */
export const QuickAlertModal = ({ open, onClose, defaults = {}, lockKind = false, title, onCreated }) => {
  const { user } = useAuth();
  const [kind, setKind] = useState(defaults.kind || "price_threshold");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setKind(defaults.kind || "price_threshold");
      setForm({ direction: "above", ...defaults });
    }
  }, [open, JSON.stringify(defaults)]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  const isPro = user?.tier === "pro";

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!isPro) {
      toast.error("Sign in to create alerts");
      return;
    }
    setBusy(true);
    try {
      const payload = { kind, ...form };
      const created = await createPriceAlert(payload);
      toast.success("Alert created");
      onCreated?.(created);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create alert");
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const KIND_TABS = [
    { id: "price_threshold", label: "Price" },
    { id: "percent_move", label: "% Move" },
    { id: "vol_breakout", label: "Vol" },
    { id: "breakeven", label: "Breakeven" },
    { id: "ltv_threshold", label: "LTV" },
    { id: "sofr_spread", label: "Spread" },
  ];

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="quick-alert-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0c0c0e] border border-white/15 rounded-xl max-w-md w-full p-5 space-y-4 shadow-2xl"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BellPlus className="w-4 h-4 text-[#FFD60A]" />
            <h3 className="text-sm font-medium tracking-tight">
              {title || "Create custom alert"}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white" data-testid="quick-alert-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!isPro && (
          <div className="text-[11px] text-[#FFD60A] bg-[#FFD60A]/10 border border-[#FFD60A]/30 rounded px-2 py-1.5">
            Pro tier required for alerts.
          </div>
        )}

        {!lockKind && (
          <div className="flex flex-wrap gap-1 bg-[#050505] border border-white/10 rounded-md p-1">
            {KIND_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setKind(t.id)}
                data-testid={`quick-alert-kind-${t.id}`}
                className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded transition ${
                  kind === t.id ? "bg-white text-black font-medium" : "text-white/55 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Kind-specific fields */}
        {kind === "price_threshold" && (
          <PriceFields form={form} set={set} />
        )}
        {kind === "percent_move" && (
          <PercentMoveFields form={form} set={set} />
        )}
        {kind === "vol_breakout" && (
          <VolBreakoutFields form={form} set={set} />
        )}
        {kind === "breakeven" && (
          <BreakevenFields form={form} set={set} />
        )}
        {kind === "ltv_threshold" && (
          <LtvFields form={form} set={set} />
        )}
        {kind === "sofr_spread" && (
          <SofrFields form={form} set={set} />
        )}

        <textarea
          value={form.note || ""}
          onChange={(e) => set("note", e.target.value.slice(0, 280))}
          placeholder="Optional note (280 chars)…"
          rows={2}
          className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[11px] text-white"
          data-testid="quick-alert-note"
        />

        <button
          type="submit"
          disabled={busy || !isPro}
          data-testid="quick-alert-submit"
          className="w-full text-[11px] bg-[#FFD60A] text-black px-3 py-2 rounded font-medium hover:bg-[#FFD60A]/90 disabled:opacity-40"
        >
          {busy ? "Creating…" : "Create alert"}
        </button>
      </form>
    </div>
  );
};

// ---- Per-kind field renderers ----
const DirectionToggle = ({ value, onChange, opts = ["above", "below"] }) => (
  <div className="inline-flex bg-[#050505] border border-white/10 rounded p-0.5">
    {opts.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onChange(o)}
        className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded inline-flex items-center gap-1 ${
          value === o ? "bg-white/10 text-white font-medium" : "text-white/55 hover:text-white"
        }`}
      >
        {o === "above" ? <ArrowUp className="w-3 h-3" /> : o === "below" ? <ArrowDown className="w-3 h-3" /> : null}
        {o}
      </button>
    ))}
  </div>
);

const Row = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">{label}</div>
    {children}
  </div>
);

const PriceFields = ({ form, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <Row label="Symbol">
      <input value={form.symbol || ""} onChange={(e) => set("symbol", e.target.value.toUpperCase())}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <Row label="Threshold">
      <input type="number" step="0.01" value={form.threshold ?? ""}
             onChange={(e) => set("threshold", parseFloat(e.target.value) || 0)}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <div className="col-span-2">
      <DirectionToggle value={form.direction || "above"} onChange={(v) => set("direction", v)} />
    </div>
  </div>
);

const PercentMoveFields = ({ form, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <Row label="Symbol">
      <input value={form.symbol || ""} onChange={(e) => set("symbol", e.target.value.toUpperCase())}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <Row label="Move (%)">
      <input type="number" step="0.1" min="0.1" value={form.percent ?? 2}
             onChange={(e) => set("percent", parseFloat(e.target.value) || 0)}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <div className="col-span-2 flex items-center gap-2">
      <DirectionToggle value={form.direction || "any"} onChange={(v) => set("direction", v)} opts={["up", "down", "any"]} />
      <select value={form.window || "intraday"} onChange={(e) => set("window", e.target.value)}
              className="bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[11px] text-white">
        <option value="intraday">Intraday (vs open)</option>
        <option value="daily">Daily (vs prev close)</option>
      </select>
    </div>
  </div>
);

const VolBreakoutFields = ({ form, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <Row label="Symbol">
      <input value={form.symbol || ""} onChange={(e) => set("symbol", e.target.value.toUpperCase())}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <Row label="Vol threshold (%)">
      <input type="number" step="0.5" min="1" value={form.vol_threshold_pct ?? 25}
             onChange={(e) => set("vol_threshold_pct", parseFloat(e.target.value) || 0)}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <div className="col-span-2 flex items-center gap-2 flex-wrap">
      <DirectionToggle value={form.direction || "above"} onChange={(v) => set("direction", v)} />
      <select value={form.vol_window || 20} onChange={(e) => set("vol_window", parseInt(e.target.value, 10))}
              className="bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[11px] text-white"
              data-testid="vol-breakout-window">
        <option value={5}>5-day RV</option>
        <option value={10}>10-day RV</option>
        <option value={20}>20-day RV</option>
        <option value={30}>30-day RV</option>
      </select>
      <span className="text-[10px] text-white/40 italic">annualized realized vol</span>
    </div>
  </div>
);


const BreakevenFields = ({ form, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <Row label="Symbol">
      <input value={form.symbol || ""} onChange={(e) => set("symbol", e.target.value.toUpperCase())}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <Row label="Breakeven">
      <input type="number" step="0.01" value={form.breakeven ?? ""}
             onChange={(e) => set("breakeven", parseFloat(e.target.value) || 0)}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <div className="col-span-2">
      <DirectionToggle value={form.direction || "below"} onChange={(v) => set("direction", v)} />
    </div>
  </div>
);

const LtvFields = ({ form, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <Row label="Threshold (%)">
      <input type="number" step="0.5" value={form.threshold_pct ?? 30}
             onChange={(e) => set("threshold_pct", parseFloat(e.target.value) || 30)}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <Row label="Collateral $">
      <input type="number" step="10000" value={form.collateral ?? 100000}
             onChange={(e) => set("collateral", parseFloat(e.target.value) || 0)}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <div className="col-span-2">
      <DirectionToggle value={form.direction || "above"} onChange={(v) => set("direction", v)} />
    </div>
  </div>
);

const SofrFields = ({ form, set }) => (
  <div className="grid grid-cols-2 gap-3">
    <Row label="Threshold (% pts)">
      <input type="number" step="0.05" value={form.threshold_pct ?? 2.0}
             onChange={(e) => set("threshold_pct", parseFloat(e.target.value) || 0)}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <Row label="Blended yield">
      <input type="number" step="0.1" min="0" value={Math.round((form.blended_yield ?? 0.11) * 100 * 10) / 10}
             onChange={(e) => set("blended_yield", (parseFloat(e.target.value) || 0) / 100)}
             className="w-full bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[12px] num" />
    </Row>
    <div className="col-span-2">
      <DirectionToggle value={form.direction || "below"} onChange={(v) => set("direction", v)} />
    </div>
  </div>
);

export default QuickAlertModal;
